// NewTrip — formulario manual para crear un viaje. La curaduría con IA llega en Fase B.
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, X, Plane, Building2, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../auth/AuthContext";

const EMPTY_FLIGHT = { type: "flight", origin: "", destination: "", date: "", time: "", airline: "", number: "", pnr: "" };
const EMPTY_HOTEL = { type: "hotel", city: "", name: "", check_in: "", check_out: "", address: "", booking_code: "" };

export default function NewTrip() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [citiesText, setCitiesText] = useState("");
  const [flights, setFlights] = useState([{ ...EMPTY_FLIGHT }]);
  const [hotels, setHotels] = useState([{ ...EMPTY_HOTEL }]);

  const updateFlight = (i, k, v) => setFlights(flights.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const updateHotel = (i, k, v) => setHotels(hotels.map((h, j) => (j === i ? { ...h, [k]: v } : h)));
  const addFlight = () => setFlights([...flights, { ...EMPTY_FLIGHT }]);
  const removeFlight = (i) => setFlights(flights.filter((_, j) => j !== i));
  const addHotel = () => setHotels([...hotels, { ...EMPTY_HOTEL }]);
  const removeHotel = (i) => setHotels(hotels.filter((_, j) => j !== i));

  const handleSave = async () => {
    if (!title || !startDate || !endDate) {
      alert("Falta título o fechas");
      return;
    }
    setSaving(true);
    try {
      const cities = citiesText.split(",").map((c) => c.trim()).filter(Boolean);
      const cleanFlights = flights.filter((f) => f.origin && f.destination);
      const cleanHotels = hotels.filter((h) => h.name && h.city);
      const data = {
        flights: cleanFlights,
        hotels: cleanHotels,
        days: [],
        places: {},
        tastings: {},
        restaurants: {},
      };
      const trip = await api("/api/trips", {
        method: "POST",
        body: JSON.stringify({
          title,
          subtitle,
          start_date: startDate,
          end_date: endDate,
          cities,
          data,
        }),
      });
      navigate(`/viaje/${trip.trip_id}`, { replace: true });
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-app pt-6 pb-28" data-testid="new-trip-page">
      <Link to="/viajes" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="new-trip-back">
        <ChevronLeft size={16} /> Mis viajes
      </Link>
      <h1 className="text-3xl font-bold leading-tight mb-1">Nuevo viaje</h1>
      <p className="text-sm text-[hsl(var(--muted))] mb-5">Paso {step} de 3</p>

      {/* Step 1: básicos */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
          <Field label="Título del viaje" required>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej: Europa + Israel 2026"
              className="w-full p-3 rounded-xl bg-white border border-[hsl(var(--line))] text-sm"
              data-testid="trip-title"
            />
          </Field>
          <Field label="Subtítulo (opcional)">
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Madrid · Budapest · Praga · Tel Aviv"
              className="w-full p-3 rounded-xl bg-white border border-[hsl(var(--line))] text-sm"
              data-testid="trip-subtitle"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio" required>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-3 rounded-xl bg-white border border-[hsl(var(--line))] text-sm"
                data-testid="trip-start"
              />
            </Field>
            <Field label="Fin" required>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-3 rounded-xl bg-white border border-[hsl(var(--line))] text-sm"
                data-testid="trip-end"
              />
            </Field>
          </div>
          <Field label="Ciudades (separadas por coma)">
            <input
              value={citiesText}
              onChange={(e) => setCitiesText(e.target.value)}
              placeholder="Madrid, Budapest, Praga, Tel Aviv"
              className="w-full p-3 rounded-xl bg-white border border-[hsl(var(--line))] text-sm"
              data-testid="trip-cities"
            />
          </Field>
          <button
            onClick={() => setStep(2)}
            disabled={!title || !startDate || !endDate}
            className="w-full py-3 rounded-2xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-semibold mt-3 disabled:opacity-40 active:scale-[0.98]"
            data-testid="trip-next-1"
          >
            Siguiente · Vuelos
          </button>
        </motion.div>
      )}

      {/* Step 2: flights */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <p className="text-sm text-[hsl(var(--muted))]">Agrega tus vuelos. Los puedes dejar vacíos y completarlos después.</p>
          {flights.map((f, i) => (
            <div key={i} className="card-paper p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))]">
                  <Plane size={11} /> Vuelo {i + 1}
                </span>
                {flights.length > 1 && (
                  <button onClick={() => removeFlight(i)} className="text-[hsl(var(--muted))]"><X size={14} /></button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Origen (SCL)" value={f.origin} onChange={(e) => updateFlight(i, "origin", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`flight-origin-${i}`} />
                <input placeholder="Destino (MAD)" value={f.destination} onChange={(e) => updateFlight(i, "destination", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`flight-dest-${i}`} />
                <input type="date" value={f.date} onChange={(e) => updateFlight(i, "date", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`flight-date-${i}`} />
                <input type="time" value={f.time} onChange={(e) => updateFlight(i, "time", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`flight-time-${i}`} />
                <input placeholder="Aerolínea (Iberia)" value={f.airline} onChange={(e) => updateFlight(i, "airline", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
                <input placeholder="N° vuelo (IB6830)" value={f.number} onChange={(e) => updateFlight(i, "number", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
                <input placeholder="PNR / Localizador" value={f.pnr} onChange={(e) => updateFlight(i, "pnr", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm col-span-2" />
              </div>
            </div>
          ))}
          <button onClick={addFlight} className="w-full py-2.5 rounded-xl border border-dashed border-[hsl(var(--line))] text-xs text-[hsl(var(--ink-soft))] active:scale-[0.98]" data-testid="add-flight">
            <Plus size={12} className="inline mr-1" /> Añadir otro vuelo
          </button>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button onClick={() => setStep(1)} className="py-3 rounded-2xl bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] font-semibold">Atrás</button>
            <button onClick={() => setStep(3)} className="py-3 rounded-2xl bg-[hsl(var(--ink))] text-[hsl(var(--bg))] font-semibold" data-testid="trip-next-2">Siguiente · Hoteles</button>
          </div>
        </motion.div>
      )}

      {/* Step 3: hotels */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
          <p className="text-sm text-[hsl(var(--muted))]">Agrega tus alojamientos. Opcional — los puedes añadir después.</p>
          {hotels.map((h, i) => (
            <div key={i} className="card-paper p-4 space-y-2">
              <div className="flex items-center justify-between mb-1">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))]">
                  <Building2 size={11} /> Hotel {i + 1}
                </span>
                {hotels.length > 1 && (
                  <button onClick={() => removeHotel(i)} className="text-[hsl(var(--muted))]"><X size={14} /></button>
                )}
              </div>
              <input placeholder="Ciudad" value={h.city} onChange={(e) => updateHotel(i, "city", e.target.value)} className="w-full p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`hotel-city-${i}`} />
              <input placeholder="Nombre del alojamiento" value={h.name} onChange={(e) => updateHotel(i, "name", e.target.value)} className="w-full p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" data-testid={`hotel-name-${i}`} />
              <input placeholder="Dirección" value={h.address} onChange={(e) => updateHotel(i, "address", e.target.value)} className="w-full p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input type="date" placeholder="Check-in" value={h.check_in} onChange={(e) => updateHotel(i, "check_in", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
                <input type="date" placeholder="Check-out" value={h.check_out} onChange={(e) => updateHotel(i, "check_out", e.target.value)} className="p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
              </div>
              <input placeholder="Código de reserva" value={h.booking_code} onChange={(e) => updateHotel(i, "booking_code", e.target.value)} className="w-full p-2 rounded-lg bg-white border border-[hsl(var(--line))] text-sm" />
            </div>
          ))}
          <button onClick={addHotel} className="w-full py-2.5 rounded-xl border border-dashed border-[hsl(var(--line))] text-xs text-[hsl(var(--ink-soft))] active:scale-[0.98]" data-testid="add-hotel">
            <Plus size={12} className="inline mr-1" /> Añadir otro hotel
          </button>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button onClick={() => setStep(2)} className="py-3 rounded-2xl bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] font-semibold">Atrás</button>
            <button onClick={handleSave} disabled={saving} className="py-3 rounded-2xl bg-[hsl(var(--accent))] text-white font-semibold disabled:opacity-40 active:scale-[0.98]" data-testid="trip-save">
              {saving ? "Guardando…" : "Crear viaje"}
            </button>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted))] text-center italic mt-2">
            Próximamente: subir tus reservas en PDF y que la IA curate el itinerario por ti.
          </p>
        </motion.div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--muted))] mb-1 block">
        {label} {required && <span className="text-[hsl(var(--accent))]">*</span>}
      </span>
      {children}
    </label>
  );
}
